/**
 * 伴旅系统 - 达人端 API（与 Apipost「伴旅系统 / 达人端」保持一致）
 *
 * 所有接口以 staffapi 开头，通过 vite proxy 代理到 https://peiwan.banly.com.cn
 * Header：Authorization: Bearer <token>
 * 统一响应格式：{ status: 200, msg: "success", data: ... }
 */
import axios from "axios";
import { http, peiwantHttp } from "@/http/axios";
import { useUserStore } from "@/store/useUserStore";
import { toastError } from "@/utils/toast";

/** 直接复用 peiwantHttp（已内置 Authorization 头），无需重复写 authHeaders */

/** BLW 后端基础域名（C端 /api 接口，用于 Apipost 测试） */
const BLW_API_BASE = "https://peiwan.banly.com.cn";

/** ==================== 基础工具 ==================== */

/** BLW 后端基础域名（达人端接口） */
const PEIWAN_HOST = "https://peiwan.banly.com.cn";

function staffPath(): string {
  const p = import.meta.env.VITE_STAFF_API_PATH || "/staffapi";
  // 开发环境使用 /staffapi 前缀（走 Vite 代理），H5/生产环境使用完整域名
  if (import.meta.env.MODE === "development") {
    return p.startsWith("/") ? p : `/${p}`;
  }
  // 生产环境：返回完整域名 + 路径前缀（如 https://peiwan.banly.com.cn/staffapi）
  return `${PEIWAN_HOST}${p.startsWith("") ? "" : "/"}${p}`;
}

/** 构建带 token 的请求头（与 /api axios 拦截器逻辑一致：Zustand + localStorage 双保险） */
function authHeaders(): Record<string, string> {
  let { token, tokenType } = useUserStore.getState();

  // 兜底：Zustand persist 可能还未从 localStorage 异步恢复，直接读 localStorage
  if (!token) {
    try {
      const raw = localStorage.getItem("user-storage");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.state?.token || parsed?.token) {
          token = parsed.state?.token ?? parsed.token;
          tokenType = parsed.state?.tokenType ?? parsed.tokenType ?? "Bearer";
        }
      }
    } catch {
      /* ignore */
    }
  }

  const h: Record<string, string> = {};
  if (token) h.Authorization = `${tokenType} ${token}`;
  return h;
}

/** 检查响应是否成功 */
function isOk(body: Record<string, unknown>): boolean {
  if (body.code === 200) return true;
  if (body.status === 200) return true;
  return false;
}

/**
 * 通用请求封装
 *
 * 后端统一响应格式：{ status: 200, msg: "success", data: T }
 * 与 Vue 版本一致：api 层返回完整响应，调用方取 .data
 * 但本层自动解包 data 字段，返回 T 类型
 */
async function request<T>(config: {
  method: string;
  url: string;
  data?: FormData | Record<string, unknown> | string[];
  params?: Record<string, string | number>;
  headers?: Record<string, string>;
}): Promise<T> {
  const url = `${staffPath()}${config.url}`;
  try {
    const res = await axios.request<Record<string, unknown>>({
      method: config.method,
      url,
      data: config.data,
      params: config.params,
      headers: { ...authHeaders(), ...config.headers },
      timeout: 30000,
    });
    const body = res.data as Record<string, unknown>;

    if (!body || typeof body !== "object") throw new Error("invalid response");
    if (!isOk(body)) {
      const msg =
        (typeof body.message === "string" && body.message) ||
        (typeof body.msg === "string" && body.msg) ||
        "请求失败";
      toastError(msg);
      throw new Error(msg);
    }
    // 后端响应 { status, msg, data }，自动解包 data 字段（与 Vue 版本的 res.data 一致）
    const data = (body as any).data;
    return (data ?? body) as T;
  } catch (e: unknown) {
    if (e instanceof Error) {
      // 已在上方 isOk 分支 toast 过的，不再重复提示
      if (e.message !== "invalid response") toastError(e.message);
      throw e;
    }
    toastError("网络异常");
    throw new Error("网络异常");
  }
}

/** ==================== 类型定义 ==================== */

/** 达人基础信息 */
export interface StaffInfo {
  id: number;
  name: string;
  thumb: string;
  score: string;
  remind_uid: number;
  phone: string;
  work_status: number; // 0 停止接单 1 可接单
  // 兼容字段（部分接口/页面会返回这些命名）
  nickname?: string;
  avatar?: string;
  role?: number;
  sex: number;
  resume: string;
  current_address: string;
  current_time: number;
  current_time_desc: string;
  place: string;
  latitude: string;
  longitude: string;
  service_radius: number;
  start_time: string;
  end_time: string;
  week_str: string[];
  city_id: number;
  city_name: string;
  idcard_no: string;
  imgs: string[];
  service_label: string[];
  emergency_contact: string;
  emergency_phone: string;
  relationship: string;
  education: string;
  star_signs: string;
  staff_height: number;
  staff_weight: number;
  content_audio: string;
  birthday: string;
  item_ids: number[];
  // 首页统计数据
  today_order: number;
  today_finish: number;
  today_start: number;
  order_count: number;
  month_order: number;
  month_finish: number;
  today_price: number;
  fans_count: number;
  praise_count: number;
}

/** 系统配置 */
export interface StaffConfig {
  staff_login_bg: string;
  staff_login_logo: string;
  staff_home_bg: string;
  staff_user_info_bg: string;
  staff_safety_bg: string;
  urgency_linkman_bg: string;
  staff_order_list_bg: string;
  service_tel: string;
  keyMap: string;
  user_extract_min_price: number;
  extract_type: string[];
  user_extract_bank: string[];
  staff_home_large_menu: MenuItem[];
  staff_home_menu: MenuItem[];
  staff_navigation_bar: NavItem[];
  material_order_status: MenuStatusItem[];
  balance_and_statistics: MenuItem[];
  staff_rest_menu: MenuItem[];
  staff_suggest_type: SuggestType[];
  massage_report_type: ReportType[];
  massage_service_label: ServiceLabel[];
}

export interface MenuItem {
  id: number;
  name: string;
  pic: string;
  url: string;
}

export interface NavItem extends MenuItem {
  pics: string;
}

export interface MenuStatusItem extends MenuItem {
  count: string | number;
}

export interface SuggestType {
  id: number;
  name: string;
}

export interface ReportType {
  id: number;
  name: string;
}

export interface ServiceLabel {
  id: number;
  name: string;
}

/** 收入统计 */
export interface IncomeData {
  balance: string;
  remain_price: string;
  not_price: number;
  reward_price: number;
  freeze_price: number;
}

/** 资金明细项 */
export interface CapitalItem {
  id: number;
  type: string;
  uid: number;
  link_id: number;
  title: string;
  number: string;
  pm: number; // 0 支出 1 收入
  mark: string;
  frozen_time: number;
  add_time: string;
  balance: string;
  is_today: number;
  is_closing: boolean;
  order?: OrderSummary;
  extract?: ExtractInfo;
}

export interface OrderSummary {
  book_time: string;
  ordersn: string;
}

export interface ExtractInfo {
  id: number;
  real_name: string;
  extract_type: string;
  bank_code: string;
  bank_address: string;
  alipay_code: string;
  extract_price: string;
  balance: string;
  fail_msg: string;
  status: number; // -1 未通过 0 审核中 1 已提现
  service_charge: string;
}

/** 打赏记录 */
export interface RewardItem {
  id: number;
  staff_id: number;
  order_id: number;
  pay_type: string;
  ordersn: string;
  pay_price: string;
  book_time_desc: string;
  item_name: string;
  order_no: string;
}

/** 分成明细项 */
export interface DivisionItem {
  id: number;
  book_time: string;
  cost_price: string;
  unit_price: string;
  num: number;
  ordersn: string;
  fare: string;
  item_name: string;
  pay_price: string;
  cost_fee: string;
  earnings: string;
  item_price: string;
  item_cost: string;
  reward_price: number;
}

/** 分成统计 */
export interface DivisionTotal {
  service_charge: number;
  fare: number;
  item_price: number;
  total_price: number;
  today_price: number;
}

/** 车费明细 */
export interface FareListResult {
  today_fare: number;
  fare_count: number;
  list: FareItem[];
}

export interface FareItem {
  id: number;
  fare: string;
  pay_time: string;
  ordersn: string;
}

/** ==================== 人员关系 ==================== */

export interface StaffChildrenItem {
  id: number;
  uid: number;
  nickname: string;
  avatar: string;
  status: number; // 1=正常 0=禁用
  create_time: number; // 秒时间戳
  is_has_children?: number | boolean;
}

/** 订单统计 */
export interface OrderStats {
  new_count: number;
  service_count: number;
  finish_count: number;
  over_count: number;
}

/** 订单列表项 */
export interface StaffOrderItem {
  id: number;
  book_time: number;
  status: number;
  item_name: string;
  service_min: number;
  add_time: number;
  travel_mode_title: string;
  fare: string;
  pay_price: string;
  cost_price: string;
  longitude: string;
  latitude: string;
  is_depart: number;
  travel_km: string;
  num: number;
  unit_price: string;
  cost_fee: string;
  unit_name: string;
  distance: number;
  status_desc: string;
  book_time_desc: string;
  add_time_desc: string;
}

/** 订单详情 */
export interface StaffOrderReportItem {
  id: number;
  type: string;
  content: string;
  imgs: string[];
  add_time: string;
}
export interface StaffOrderDetail {
  // 基础信息
  id: number;
  uid: number;
  ordersn: string;
  ordersn2: number;
  city_id: number;
  status: number; // -20已退款 -10退款申请 -1已取消 0待付款 10待接单 20待服务 30进行中 40待评价 50已完成
  is_del: number;
  delete_time: number;
  is_comment: number; // 用户是否评价订单：0可评价，1已评价
  is_staff_comment: number; // 技师是否评价订单：0未评价，1已评价
  is_submit: number;
  is_new_user: number; // 是否新客：0否，1是

  // 达人信息
  service_staff_id: number; // 达人/技师ID
  staff_name: string;
  staff_thumb: string;
  staff_phone: string;
  staff_longitude: string;
  staff_latitude: string;
  staff_address: string;
  staff_remark: string | null;
  staff_imgs: string | null;

  // 服务项目
  item_id: number;
  item_thumb: string;
  item_name: string;
  unit_price: string;
  item_price: string;
  item_min: number;
  accept_order_type: number; // 下单类型
  unit_name: string;
  num: number; // 服务数量
  service_min: number; // 服务分钟数

  // 价格相关
  cost_price: string;
  cost_rate: string; // 技师收益比例
  cost_fee: string; // 技师收益
  original_price: string;
  coupon_id: number;
  coupon_price: string;
  discount_price: string;
  pay_price: string;
  upgrade_price: string; // 订单升级差价总额
  subjoin_price: string;
  subjoin_cost: string; // 订单加钟技师收益

  // 支付相关
  pay_type: string; // 支付类型：yue余额，offline线下，weixin微信
  trade_no: string; // 支付流水号
  pay_time: number;
  pay_type_desc: string;
  is_pay: number;

  // 时间相关
  book_time: number;
  book_time_desc: string;
  appoint_time: number;
  rob_time: number;
  handle_time: number;
  finish_time: number;
  cancel_time: number;
  add_time: number;
  add_time_desc: string;
  finish_time_desc: string;
  cancel_time_desc: string;
  comment_time: number;
  staff_comment_time: number;
  no_pay_time: number;
  no_pay_time_desc: string;
  no_take_time: number;
  no_take_time_desc: string;

  // 服务进度
  is_depart: number; // 是否出发：0未出发 1已出发 2已到达 3已打卡
  depart_time: number;
  arrive_time: number;
  start_time: number;
  is_overtime: number; // 是否超时：0否 1是
  service_count_down?: number;

  // 打卡信息
  sign_time: number;
  sign_longitude: string;
  sign_latitude: string;
  sign_address: string;
  sign_imgs: string;
  sign_content: string;

  // 地址相关
  link_name: string;
  link_mobile: string;
  link_sex: string | null;
  province: string;
  city: string;
  district: string;
  user_address: string;
  longitude: string;
  latitude: string;

  // 出行相关
  is_fare: number; // 车费类型，1不用车费，2单程，3双程
  fare: string;
  travel_mode_id: number;
  travel_mode_title: string;
  start_km: string;
  start_fee: string;
  over_fee: string;
  travel_km: string;
  fee_km: string;
  distance: number;

  // 备注
  remark: string;
  remark_saler: string | null;
  cancel_reason: string | null;

  // 分佣
  spread_uid: number;
  spread_two_uid: number;
  one_brokerage: string;
  two_brokerage: string;

  // 其他
  refund_id: number;
  upgrade_id: number;
  is_private_number: number;
  report_count: number;
  is_up: boolean;
  is_subjoin: boolean;
  reward_count: number;
  score: string; // 达人评分
  service_score: string; // 技师收益
  form_count?: number;
  accept_order_type?: number;
  handle_time_desc?: string;
  depart_time_desc?: string;
  arrive_time_desc?: string;
  start_time_desc?: string;
  finish_time_desc?: string;

  // 打卡地址
  depart_addr?: string;   // 出发打卡地址
  arrive_addr?: string;   // 到达打卡地址
  start_addr?: string;    // 服务开始打卡地址
  finish_addr?: string;   // 完成打卡地址

  base_item_cost?: string;
  cost_fee_sum?: string;
  sign_time_desc?: string;
}

/** 项目 */
export interface ItemDetail {
  id: number;
  name: string;
  pic: string;
  price: string;
  unit_name: string;
}

/** 可申请项目列表项 — 对应 getItemList 返回结构 */
export interface StaffCateItem {
  id: number;
  name: string; // 项目名称
  pic: string; // 缩略图
  price: string; // 价格
  short_title?: string; // 简介（兼容）
  thumb?: string; // 兼容旧字段
}

/** 已申请项目列表项（我的项目） */
export interface StaffHasCateItem {
  id: number;
  item_id: number;
  title: string;
  thumb: string;
  short_title: string;
  check_status?: number; // 1=待审核, 2=通过(不显示), 3=未通过
}

/** 项目详情（含价格、资质等） */
export interface StaffCateItemDetail {
  id: number;
  title: string;
  thumb: string;
  short_title: string;
  accept_order_type: number; // 0=全部 1=仅小时 2=仅日单
  price: string;
  day_order_money: string;
  image: string;
  images?: unknown;
  number: string;
  is_qualification?: boolean;
  check_status?: number;
}

/** 更新项目表单参数 */
export interface StaffCateItemUpdateQuery {
  id: number;
  accept_order_type: number;
  price: string;
  day_order_money: string;
  image: string;
  number: string;
}

/** 已关联项目数据 */
export interface StaffHasCateData {
  nameStr: string;
  ids: number[];
}

/** ==================== 评价记录 ==================== */

export interface StaffCommentKV {
  title: string;
  value: string;
}

export interface StaffCommentItem {
  id: number;
  uid: number;
  item_name: string;
  book_time: string;
  name: string; // 服务人员
  form_content: StaffCommentKV[];
  add_time: string;
}

/** 分页结果 */
export interface PaginatedResult<T> {
  list: T[];
  count: number;
}

/** ==================== 我的 ==================== */

/** 达人信息 + 配置 + 收入（我的页面） */
interface MyConfigResult {
  staff: Pick<
    StaffInfo,
    "id" | "name" | "thumb" | "score" | "remind_uid" | "phone"
  >;
  config: StaffConfig;
  income: IncomeData;
}
export async function getMyConfig(): Promise<MyConfigResult> {
  return request<MyConfigResult>({ method: "GET", url: "/my_config" });
}

/** 系统配置（标签、银行、客服电话等） */
export async function getStaffConfig(): Promise<StaffConfig> {
  return request<StaffConfig>({ method: "GET", url: "/config" });
}

/** 城市选项（返回完整配置） */
export async function getCityList(): Promise<StaffConfig> {
  return request<StaffConfig>({ method: "GET", url: "/city/list" });
}

/** 保存资料 form-data */
export async function saveStaffData(data: FormData): Promise<StaffConfig> {
  return request<StaffConfig>({
    method: "POST",
    url: "/update/staff_data",
    data,
  });
}

/** 保存紧急联系人 */
export async function saveEmergency(data: {
  emergency_contact: string;
  emergency_phone: string;
}): Promise<void> {
  return request<void>({ method: "POST", url: "/updateEmergency", data });
}

/** ==================== 资金 ==================== */

/** 收入统计 */
export async function getIncome(): Promise<IncomeData> {
  return request<IncomeData>({ method: "GET", url: "/getIncome" });
}

/** 资金明细列表 */
export async function getCapitalList(params: {
  page?: number;
  limit?: number;
  pm?: string; // ''|'0'=支出 '1'=收入
  start_time?: string;
  end_time?: string;
}): Promise<CapitalItem[]> {
  return request<CapitalItem[]>({
    method: "GET",
    url: "/getStaffCapitalList",
    params: params as Record<string, string | number>,
  });
}

/** 提现 */
export async function createExtract(data: {
  extract_price: string;
  extract_type: "weixin" | "alipay" | "bank";
  qrcode_url?: string; // 微信/支付宝传
  bank_address?: string; // 银行传
  bank_code?: string; // 银行卡号
  real_name?: string; // 银行真实姓名
}): Promise<void> {
  return request<void>({ method: "POST", url: "/createExtract", data });
}

/** ==================== 打赏 ==================== */

/** 打赏记录列表 */
export async function getRewardList(params: {
  page?: number;
  limit?: number;
  start_time?: string;
  end_time?: string;
}): Promise<RewardItem[]> {
  return request<RewardItem[]>({
    method: "GET",
    url: "/getRewardList",
    params: params as Record<string, string | number>,
  });
}

/** ==================== 分成明细 ==================== */

/** 分成明细列表 */
export async function getDivisionList(params: {
  page: number;
  limit: number;
  start_time?: string;
  end_time?: string;
}): Promise<PaginatedResult<DivisionItem> & Partial<DivisionTotal>> {
  return request<PaginatedResult<DivisionItem> & Partial<DivisionTotal>>({
    method: "GET",
    url: "/division/detail",
    params: params as Record<string, string | number>,
  });
}

/** 分成总计 */
export async function getDivisionTotal(): Promise<DivisionTotal> {
  return request<DivisionTotal>({ method: "GET", url: "/division/total" });
}

/** ==================== 问题反馈 ==================== */

/** 提交反馈 */
export async function submitSuggestion(data: {
  type: string;
  imgs?: string[]; // 图片 URL 数组
  content: string;
}): Promise<void> {
  return request<void>({ method: "POST", url: "/createStaffSuggest", data });
}

/** ==================== 退出 ==================== */

/** 退出登录 */
export async function logout(): Promise<void> {
  return request<void>({ method: "POST", url: "/logout" });
}

/** ==================== 首页 ==================== */

/** 首页综合数据（达人详情 + 今日/本月统计 + 位置） */
export async function getStaffDetail(): Promise<StaffInfo> {
  return request<StaffInfo>({ method: "GET", url: "/staff/detail" });
}

/** 更新经纬度位置 */
export async function updateAddress(params: {
  current_latitude: string;
  current_longitude: string;
  current_address: string;
}): Promise<void> {
  return request<void>({
    method: "POST",
    url: "/updateAddress",
    data: params as Record<string, unknown>,
  });
}

/** 车费明细 */
export async function getFareList(params: {
  page: number;
  limit: number;
  start_time?: string;
  end_time?: string;
}): Promise<FareListResult> {
  return request<FareListResult>({
    method: "GET",
    url: "/fare/list",
    params: params as Record<string, string | number>,
  });
}

/** 切换工作状态 0=停止 1=可接 */
export async function setWorkStatus(status: 0 | 1): Promise<void> {
  return request<void>({ method: "GET", url: `/setWork/${status}` });
}

/** ==================== 相册 ==================== */

/** 我的相册图片列表 */
export async function getMyImages(): Promise<string[]> {
  return request<string[]>({ method: "GET", url: "/get_imgs" });
}

/** 上传相册图片 */
export async function uploadImgs(file: File): Promise<string> {
  const fd = new FormData();
  fd.append("file", file);
  return request<string>({ method: "POST", url: "/upload_imgs", data: fd });
}

/** 更新/保存相册图片列表 — 传入图片 URL 数组 */
export async function updateMyAlbums(data: { imgs: string[] }): Promise<void> {
  return request<void>({
    method: "POST",
    url: "/upload_imgs",
    data,
  });
}

/** ==================== 接单设置 ==================== */

/** 更新接单时段 */
export async function updateTakeTime(data: FormData): Promise<void> {
  return request<void>({ method: "POST", url: "/updateTakeTime", data });
}

/** 更新接单位置和半径 */
export async function updateTakeAddress(params: {
  latitude: string | number;
  longitude: string | number;
  place: string;
  service_radius: number;
}): Promise<void> {
  const fd = new FormData();
  fd.append("latitude", String(params.latitude));
  fd.append("longitude", String(params.longitude));
  fd.append("place", params.place);
  fd.append("service_radius", String(params.service_radius));
  return request<void>({ method: "POST", url: "/updateTakeAddress", data: fd });
}

/** ==================== 项目 ==================== */

/** 项目申请列表 */
export async function getItemList(params: {
  page?: number;
  limit?: number;
}): Promise<PaginatedResult<ItemDetail>> {
  return request<PaginatedResult<ItemDetail>>({
    method: "GET",
    url: "/getItemList",
    params: params as Record<string, string | number>,
  });
}

/** 项目详情 */
export async function getItemDetail(id: number): Promise<ItemDetail> {
  return request<ItemDetail>({
    method: "GET",
    url: `/getItemDetail`,
    params: { id },
  });
}

/** 更新我的项目 */
export async function updateMyItem(data: FormData): Promise<void> {
  return request<void>({ method: "POST", url: "/updateMyItem", data });
}

/** 申请项目列表 */
export async function getApplyItemList(params: {
  page?: number;
  limit?: number;
}): Promise<PaginatedResult<ItemDetail>> {
  return request<PaginatedResult<ItemDetail>>({
    method: "GET",
    url: "/getApplyItemList",
    params: params as Record<string, string | number>,
  });
}

/** 更新项目 */
export async function updateItem(data: FormData): Promise<void> {
  return request<void>({ method: "POST", url: "/updateItem", data });
}

/** 批量查项目名称 ids=7,9,10 */
export async function getItemNames(ids: string): Promise<ItemDetail[]> {
  return request<ItemDetail[]>({
    method: "GET",
    url: "/getItemName",
    params: { ids },
  });
}

/** 可申请项目列表（项目申请页）— 调用 staffapi/getItemList */
export async function getStaffCateItem(params: {
  page?: number;
  limit?: number;
}): Promise<PaginatedResult<StaffCateItem>> {
  return request<PaginatedResult<StaffCateItem>>({
    method: "GET",
    url: "/getItemList",
    params: params as Record<string, string | number>,
  });
}

/** 已申请项目列表（我的项目页） */
export async function getStaffHasCateItemList(params: {
  page?: number;
  limit?: number;
}): Promise<PaginatedResult<StaffHasCateItem>> {
  return request<PaginatedResult<StaffHasCateItem>>({
    method: "GET",
    // 已申请项目列表（后端实际接口）
    url: "/getApplyItemList",
    params: params as Record<string, string | number>,
  });
}

/** 单个项目详情 */
export async function getStaffCateItemDetail(params: {
  id: number;
}): Promise<StaffCateItemDetail> {
  return request<StaffCateItemDetail>({
    method: "GET",
    url: "/getItemDetail",
    params: params as Record<string, string | number>,
  });
}

/** 获取已关联项目的名称列表 */
export async function getStaffHasCateList(params: {
  ids: string;
}): Promise<StaffHasCateData> {
  return request<StaffHasCateData>({
    method: "GET",
    url: "/getItemName",
    params: params as Record<string, string>,
  });
}

/** 更新已申请项目（提交 item_ids） */
export async function updateStaffItem(data: FormData): Promise<void> {
  return request<void>({ method: "POST", url: "/updateMyItem", data });
}

/** 更新单个项目详情（价格、资质等） */
export async function updateStaffCateItem(data: FormData): Promise<void> {
  return request<void>({ method: "POST", url: "/updateMyItem", data });
}

/** ==================== 人员关系 ==================== */

/** 下级列表 */
export async function getChildren(
  uid?: string | number,
): Promise<StaffChildrenItem[]> {
  const params = uid !== undefined ? { uid: String(uid) } : undefined;
  return request<StaffChildrenItem[]>({
    method: "GET",
    url: "/staff/children",
    params: params as any,
  });
}

/** ==================== 订单 ==================== */

/** 订单统计（新订单/服务中/已完成/超时） */
export async function getOrderStats(): Promise<OrderStats> {
  return request<OrderStats>({ method: "GET", url: "/getOrderData" });
}

/** 订单列表 status: 10待接单 20待服务 30进行中 40待评价 50已完成 */
export async function getOrderList(params: {
  page: number;
  limit: number;
  status?: string;
}): Promise<PaginatedResult<StaffOrderItem>> {
  return request<PaginatedResult<StaffOrderItem>>({
    method: "GET",
    url: "/massage/order/list",
    params: params as Record<string, string | number>,
  });
}

/** 订单详情
 *  接口: GET {BLW_API_BASE}/api/massage/order/detail/{id}
 */
export async function getOrderDetail(id: number): Promise<StaffOrderDetail> {
  const res = await http.get<any>(`${BLW_API_BASE}/api/massage/order/detail/${id}`);

  console.log("[getOrderDetail] HTTP Status:", res.status);
  console.log("[getOrderDetail] Response:", res.data);

  if (!res.data || typeof res.data !== "object") throw new Error("invalid response");

  // { status: 200, msg: "success", data: {...} }
  if ("data" in res.data && res.data.data) return res.data.data as StaffOrderDetail;
  // 直接数据对象
  if ("id" in res.data || "ordersn" in res.data) return res.data as StaffOrderDetail;
  
  throw new Error(res.data?.msg || "订单不存在");
}

/** 确认接单 */
export async function acceptOrder(orderId: number): Promise<StaffOrderDetail> {
  return request<StaffOrderDetail>({
    method: "GET",
    url: `/order/accept/${orderId}`,
  });
}

/** 取消接单 */
export async function cancelAccept(orderId: number): Promise<StaffOrderDetail> {
  return request<StaffOrderDetail>({
    method: "GET",
    url: `/cancelAccept/${orderId}`,
  });
}

/** 用户表单数据 */
export async function getUserFormData(params: {
  page: number;
  limit: number;
  uid: number;
}): Promise<unknown> {
  return request<unknown>({
    method: "GET",
    url: "/getUserFormData",
    params: params as Record<string, string | number>,
  });
}

/** 出发核验 */
export async function departVerify(orderId: number): Promise<void> {
  return request<void>({
    method: "GET",
    url: `/order/depart_verify?order_id=${orderId}`,
  });
}

/** 出发确认（携带经纬度+详细地址） */
export async function departOrder(orderId: number, location?: { lat: number; lng: number; address?: string }): Promise<void> {
  return request<void>({
    method: "POST",
    url: `/order/depart/${orderId}`,
    data: location ? { lat: location.lat, lng: location.lng, address: location.address || "" } : undefined,
  });
}

/** 到达确认（携带经纬度+详细地址） */
export async function arriveOrder(orderId: number, location?: { lat: number; lng: number; address?: string }): Promise<void> {
  return request<void>({
    method: "POST",
    url: `/order/arrive/${orderId}`,
    data: location ? { lat: location.lat, lng: location.lng, address: location.address || "" } : undefined,
  });
}

/** 开始服务（携带经纬度+详细地址） */
export async function startService(orderId: number, location?: { lat: number; lng: number; address?: string }): Promise<void> {
  return request<void>({
    method: "POST",
    url: `/order/start/${orderId}`,
    data: location ? { lat: location.lat, lng: location.lng, address: location.address || "" } : undefined,
  });
}

/** 完成服务核验 */
export async function finishVerify(
  orderId: number,
): Promise<{ is_finish: boolean; msg: string }> {
  return request<{ is_finish: boolean; msg: string }>({
    method: "GET",
    url: `/order/finish_verify?order_id=${orderId}`,
  });
}

/** 完成服务 */
export async function finishOrder(orderId: number): Promise<void> {
  return request<void>({
    method: "POST",
    url: `/order/finish?order_id=${orderId}`,
  });
}

/** 订单上报/报备 */
export async function createOrderReport(data: FormData): Promise<void> {
  return request<void>({ method: "POST", url: "/createOrderReport", data });
}

/** 打卡核验 lat1/lon1=当前 lat2/lon2=目标 */
export async function punchCardVerify(params: {
  lat1: number;
  lon1: number;
  lat2: number;
  lon2: number;
}): Promise<void> {
  return request<void>({
    method: "GET",
    url: `/order/punch_card_verify`,
    params: params as Record<string, string | number>,
  });
}

/** 打卡 */
export async function punchCard(data: FormData): Promise<void> {
  return request<void>({ method: "POST", url: "/order/punch_card", data });
}

/** 到达地点提交打卡（对齐 uni-app: staffPunchCard，携带经纬度+详细地址） */
export async function submitPunchCard(data: {
  order_id: number;
  content: string;
  imgs: string[];
  lat?: number;
  lng?: number;
  address?: string;
}): Promise<void> {
  const fd = new FormData();
  fd.append("order_id", String(data.order_id));
  fd.append("content", data.content || "");
  (data.imgs || []).forEach((u) => fd.append("imgs[]", u));
  if (data.lat != null) fd.append("lat", String(data.lat));
  if (data.lng != null) fd.append("lng", String(data.lng));
  if (data.address) fd.append("address", data.address);
  return punchCard(fd);
}

/** 服务完成提交打卡并完成服务（对齐 uni-app: staffOrderFinish，携带经纬度+详细地址） */
export async function submitOrderFinish(data: {
  order_id: number;
  content: string;
  imgs: string[];
  lat?: number;
  lng?: number;
  address?: string;
}): Promise<void> {
  const fd = new FormData();
  fd.append("order_id", String(data.order_id));
  fd.append("content", data.content || "");
  (data.imgs || []).forEach((u) => fd.append("imgs[]", u));
  if (data.lat != null) fd.append("lat", String(data.lat));
  if (data.lng != null) fd.append("lng", String(data.lng));
  if (data.address) fd.append("address", data.address);
  return request<void>({
    method: "POST",
    url: `/order/finish?order_id=${data.order_id}`,
    data: fd,
  });
}

/** 默认表单 */
export async function getDefaultForm(): Promise<unknown> {
  return request<unknown>({ method: "GET", url: "/form/default" });
}

/** 订单评价 */
export async function commentOrder(data: FormData): Promise<void> {
  return request<void>({ method: "POST", url: "/order/comment", data });
}

/** 评价记录列表（uni-app: getStaffCommentLists） */
export async function getStaffCommentLists(params: {
  uid: number;
  page: number;
  limit: number;
}): Promise<PaginatedResult<StaffCommentItem>> {
  // 实际接口：/getUserFormData
  const res = await request<any>({
    method: "GET",
    url: "/getUserFormData",
    params: params as Record<string, string | number>,
  });
  // 兼容后端可能返回数组或分页对象
  if (Array.isArray(res)) {
    return {
      list: res as StaffCommentItem[],
      count: (res as StaffCommentItem[]).length,
    };
  }
  if (res && typeof res === "object") {
    const list = Array.isArray(res.list)
      ? res.list
      : Array.isArray(res.data)
        ? res.data
        : [];
    const count =
      typeof res.count === "number"
        ? res.count
        : typeof res.total === "number"
          ? res.total
          : list.length;
    return { list, count };
  }
  return { list: [], count: 0 };
}

/** ==================== 邀请成为达人 ==================== */

/** 邀请成为达人二维码 */
export interface ExpertInviteQrCode {
  code_url: string; // 二维码图片地址
}

/** 获取邀请成为达人的二维码 */
export async function getExpertInviteQrCode(): Promise<ExpertInviteQrCode> {
  const res = await request<ExpertInviteQrCode>({
    method: "GET",
    url: "/code",
    headers: { "Form-type": "h5" },
  });
  return res;
}

/** ==================== 短信验证码 ==================== */

/** 发送验证码参数 */
export interface SendSmsParams {
  phone: string; // 手机号
  type: "apply_talent" | "change_phone" | "login" | "forget_pwd"; // 验证码类型
}

/** 发送验证码响应 */
export interface SendSmsResponse {
  expire: number; // 验证码有效期(秒)
  code?: string; // 验证码（仅开发/测试环境返回）
}

/** 发送短信验证码 - POST home/sms/send
 * @description 向指定手机号发送短信验证码，支持登录、注册、忘记密码、绑定手机、申请达人等场景
 * @param phone 手机号
 * @param type 验证码类型：apply_talent申请达人 | change_phone修改手机号 | login登录 | forget_pwd忘记密码
 */
export async function sendSmsCode(phone: string, type: SendSmsParams["type"]): Promise<SendSmsResponse> {
  console.log("[sendSmsCode] 请求参数:", { phone, type, url: "/home/sms/send" });
  // 用户端接口，不走 staffapi 前缀，使用 http（baseURL: /api）
  const res = await http.post<SendSmsResponse>("/home/sms/send", { phone, type });
  console.log("[sendSmsCode] 响应:", res.data);
  return res.data;
}

/** ==================== 城市列表 ==================== */

/** 城市列表响应 */
export interface CityItem {
  city_id: number;
  city_name: string;
  parent_id: number;
}

export interface CityListResponse {
  status: number;
  msg: string;
  data: CityItem[];
}

/** 获取城市列表 - GET /peiwan/api/massage/city/list */
export async function getMassageCityList(): Promise<CityListResponse> {
  const res = await peiwantHttp.get<CityListResponse>("/api/massage/city/list");
  return res.data;
}

/** ==================== 提交申请 ==================== */

/** 提交申请参数 */
export interface StaffApplyParams {
  link_mobile: string;    // 联系人手机
  city_id: string;       // 意向城市ID
  short_desc: string;     // 个人简介
  link_name: string;      // 联系人
  sex: string;           // 性别：1男, 2女, 0未知
  work_time: string;      // 从业时间
  captcha: string;        // 验证码
  spread?: string;        // 推广码
}

/** 提交申请响应 */
export interface StaffApplyResponse {
  status: number;
  msg: string;
}

/** 提交达人申请 - POST /peiwan/api/massage/staff/apply
 * @description 用户提交成为达人的申请
 */
export async function submitStaffApply(params: StaffApplyParams): Promise<StaffApplyResponse> {
  console.log("[submitStaffApply] 请求参数:", params);
  // 使用 peiwantHttp（已内置 Authorization: {token}，与 BLW 旧系统认证格式一致）
  const res = await peiwantHttp.post<StaffApplyResponse>("/api/massage/staff/apply", params);
  console.log("[submitStaffApply] 响应:", res.data);
  return res.data;
}
